import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const UserTable = ({
  users,
  selectedUsers,
  sortConfig,
  onSort,
  onUserSelect,
  onSelectAll,
  onViewDetails,
  onDeleteUser,
  allSelected
}) => {
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, user: null });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success-50 border-success-200';
      case 'inactive': return 'text-secondary-600 bg-secondary-50 border-secondary-200';
      case 'pending': return 'text-warning bg-warning-50 border-warning-200';
      case 'suspended': return 'text-error bg-error-50 border-error-200';
      default: return 'text-secondary-600 bg-secondary-50 border-secondary-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Активен';
      case 'inactive': return 'Неактивен';
      case 'pending': return 'Ожидает';
      case 'suspended': return 'Заблокирован';
      default: return status;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Никогда';
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleContextMenu = (e, user) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      user
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, user: null });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  React.useEffect(() => {
    const handleClick = () => handleCloseContextMenu();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-secondary-200">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded border-secondary-300 text-primary focus:ring-primary/20"
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Имя
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('email')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Email
                  <Icon name={getSortIcon('email')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('role')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Роль
                  <Icon name={getSortIcon('role')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Статус
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('lastLogin')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Последний вход
                  <Icon name={getSortIcon('lastLogin')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('registrationDate')}
                  className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-primary transition-colors duration-150"
                >
                  Дата регистрации
                  <Icon name={getSortIcon('registrationDate')} size={14} />
                </button>
              </th>
              <th className="w-24 px-4 py-3 text-center">
                <span className="text-sm font-medium text-text-primary">Действия</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {users.map((user) => (
              <tr
                key={user.id}
                className="hover:bg-secondary-25 transition-colors duration-150"
                onContextMenu={(e) => handleContextMenu(e, user)}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => onUserSelect(user.id, e.target.checked)}
                    className="rounded border-secondary-300 text-primary focus:ring-primary/20"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{user.name}</p>
                      <p className="text-sm text-text-secondary">{user.department}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-primary">{user.email}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-primary">{user.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(user.status)}`}>
                    {getStatusText(user.status)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-secondary">
                    {formatDate(user.lastLogin)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-secondary">
                    {formatDate(user.registrationDate)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onViewDetails(user)}
                      className="p-1 rounded hover:bg-secondary-100 transition-colors duration-150"
                      title="Просмотр деталей"
                    >
                      <Icon name="Eye" size={16} className="text-secondary-500" />
                    </button>
                    <button
                      onClick={() => {}}
                      className="p-1 rounded hover:bg-secondary-100 transition-colors duration-150"
                      title="Редактировать"
                    >
                      <Icon name="Edit" size={16} className="text-secondary-500" />
                    </button>
                    <button
                      onClick={() => onDeleteUser(user)}
                      className="p-1 rounded hover:bg-error-50 hover:text-error transition-colors duration-150"
                      title="Удалить"
                    >
                      <Icon name="Trash2" size={16} className="text-secondary-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-secondary-100">
        {users.map((user) => (
          <div key={user.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => onUserSelect(user.id, e.target.checked)}
                  className="rounded border-secondary-300 text-primary focus:ring-primary/20 mt-1"
                />
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-text-primary">{user.name}</p>
                  <p className="text-sm text-text-secondary">{user.email}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(user.status)}`}>
                {getStatusText(user.status)}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <p className="text-text-secondary">Роль:</p>
                <p className="text-text-primary font-medium">{user.role}</p>
              </div>
              <div>
                <p className="text-text-secondary">Отдел:</p>
                <p className="text-text-primary font-medium">{user.department}</p>
              </div>
              <div>
                <p className="text-text-secondary">Последний вход:</p>
                <p className="text-text-primary">{formatDate(user.lastLogin)}</p>
              </div>
              <div>
                <p className="text-text-secondary">Регистрация:</p>
                <p className="text-text-primary">{formatDate(user.registrationDate)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onViewDetails(user)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-secondary-200 rounded-md hover:bg-secondary-50 transition-colors duration-150"
              >
                <Icon name="Eye" size={16} />
                Просмотр
              </button>
              <button
                onClick={() => {}}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-secondary-200 rounded-md hover:bg-secondary-50 transition-colors duration-150"
              >
                <Icon name="Edit" size={16} />
                Изменить
              </button>
              <button
                onClick={() => onDeleteUser(user)}
                className="px-3 py-2 border border-error-200 text-error rounded-md hover:bg-error-50 transition-colors duration-150"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          className="fixed bg-surface border border-secondary-200 rounded-md shadow-lg py-2 z-50 min-w-48"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              onViewDetails(contextMenu.user);
              handleCloseContextMenu();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary-50 transition-colors duration-150 text-left"
          >
            <Icon name="Eye" size={16} className="text-secondary-500" />
            Просмотр деталей
          </button>
          <button
            onClick={() => {
              handleCloseContextMenu();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary-50 transition-colors duration-150 text-left"
          >
            <Icon name="Edit" size={16} className="text-secondary-500" />
            Редактировать
          </button>
          <button
            onClick={() => {
              handleCloseContextMenu();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary-50 transition-colors duration-150 text-left"
          >
            <Icon name="Key" size={16} className="text-secondary-500" />
            Сбросить пароль
          </button>
          <div className="border-t border-secondary-100 my-1"></div>
          <button
            onClick={() => {
              onDeleteUser(contextMenu.user);
              handleCloseContextMenu();
            }}
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-error-50 hover:text-error transition-colors duration-150 text-left"
          >
            <Icon name="Trash2" size={16} className="text-secondary-500" />
            Удалить пользователя
          </button>
        </div>
      )}
    </>
  );
};

export default UserTable;